import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { X, Search, ChevronDown, ChevronUp, Plus, Minus, Image as ImageIcon, Edit2, Trash2, GripVertical, FileText, ClipboardList, Tag, Target, Check, ArrowUp, CheckCircle2, BookOpen, Home, ChevronRight, Eye } from 'lucide-react';
import { getTags } from '../../actions/tagActions';

const MAX_WIDTH = 800; // Maximum width for thumbnail
const QUALITY = 0.7; // Image quality (0.7 = 70% quality)

const CourseForm = ({ mode = 'new', course = null, lessons = [], submissionForms = [], reinforcements = [], userRole = 'admin', onClose, onSubmit, tag: { tags }, getTags }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [includedLessons, setIncludedLessons] = useState([]);
  const [availableLessons, setAvailableLessons] = useState([]);
  const [includedSubmissionForms, setIncludedSubmissionForms] = useState([]);
  const [availableSubmissionForms, setAvailableSubmissionForms] = useState([]);
  const [includedReinforcements, setIncludedReinforcements] = useState([]);
  const [availableReinforcements, setAvailableReinforcements] = useState([]);
  const [contentOrder, setContentOrder] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchContext, setSearchContext] = useState('all');
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [consecutiveReinforcementWarning, setConsecutiveReinforcementWarning] = useState(false);
  const tagDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // New UI/UX enhancement states
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isContentOrderCollapsed, setIsContentOrderCollapsed] = useState(false);
  const [isLessonsCollapsed, setIsLessonsCollapsed] = useState(false);
  const [isSubmissionFormsCollapsed, setIsSubmissionFormsCollapsed] = useState(false);
  const [isReinforcementsCollapsed, setIsReinforcementsCollapsed] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedBatchItems, setSelectedBatchItems] = useState([]);
  const [hoveredContent, setHoveredContent] = useState(null);
  const [dragOverThumbnail, setDragOverThumbnail] = useState(false);
  const formRef = useRef(null);
  const thumbnailModified = useRef(false);

  useEffect(() => {
    getTags();
  }, [getTags]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(event.target)) {
        setShowTagDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check for consecutive reinforcements whenever content order changes
  useEffect(() => {
    if (contentOrder.length > 0) {
      const sortedContent = [...contentOrder].sort((a, b) => a.order - b.order);
      let hasConsecutive = false;

      for (let i = 0; i < sortedContent.length - 1; i++) {
        if (sortedContent[i].type === 'reinforcement' && sortedContent[i + 1].type === 'reinforcement') {
          hasConsecutive = true;
          break;
        }
      }

      setConsecutiveReinforcementWarning(hasConsecutive);
    } else {
      setConsecutiveReinforcementWarning(false);
    }
  }, [contentOrder]);

  // Scroll detection for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top handler
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const safeLessons = Array.isArray(lessons) ? lessons : [];
    const safeForms = Array.isArray(submissionForms) ? submissionForms : [];
    const safeReinforcements = Array.isArray(reinforcements) ? reinforcements : [];

    setIsLoading(true);
    try {
      if (mode === 'edit' || mode === 'view') {
        setTitle(course?.title || '');
        setDescription(course?.description || '');
        setSelectedTags(course?.tags ? course.tags.map(tag => tag._id) : []);
        // Handle existing thumbnail — skip if user already picked a new one
        if (!thumbnailModified.current) {
          if (course?.thumbnail?.url) {
            setThumbnail(course.thumbnail);
            setThumbnailPreview(course.thumbnail.url);
          } else if (course?.thumbnail?.data && course?.thumbnail?.contentType) {
            setThumbnail(course.thumbnail);
            setThumbnailPreview(`data:${course.thumbnail.contentType};base64,${course.thumbnail.data}`);
          } else {
            setThumbnail(null);
            setThumbnailPreview('');
          }
        }
        
        // Handle lessons
        const included = safeLessons.filter(l => course?.lessons?.some(id => (l._id === id || (typeof id === 'object' && l._id === id._id))));
        setIncludedLessons(included);
        const available = safeLessons.filter(l => !course?.lessons?.some(id => (l._id === id || (typeof id === 'object' && l._id === id._id))));
        setAvailableLessons(available);

        // Handle submission forms - only non-repeatable forms can be assigned to courses
        const includedForms = safeForms.filter(sf =>
          !sf.repeatable && course?.submissionForms?.some(id => (sf._id === id || (typeof id === 'object' && sf._id === id._id)))
        );
        setIncludedSubmissionForms(includedForms);
        const availableForms = safeForms.filter(sf =>
          !sf.repeatable && !course?.submissionForms?.some(id => (sf._id === id || (typeof id === 'object' && sf._id === id._id)))
        );
        setAvailableSubmissionForms(availableForms);

        // Handle reinforcements
        const includedReinforcementItems = safeReinforcements.filter(r =>
          course?.reinforcements?.some(id => (r._id === id || (typeof id === 'object' && r._id === id._id)))
        );
        setIncludedReinforcements(includedReinforcementItems);
        const availableReinforcementItems = safeReinforcements.filter(r =>
          !course?.reinforcements?.some(id => (r._id === id || (typeof id === 'object' && r._id === id._id)))
        );
        setAvailableReinforcements(availableReinforcementItems);
        
        // Handle content order
        if (course?.contentOrder && course.contentOrder.length > 0) {
          setContentOrder(course.contentOrder);
        } else {
          // Generate default order from existing content
          const defaultOrder = [];
          let orderIndex = 0;
          
          // Add lessons in their current order
          course?.lessons?.forEach(lessonId => {
            defaultOrder.push({
              type: 'lesson',
              id: lessonId,
              order: orderIndex++
            });
          });
          
          // Add submission forms in their current order
          course?.submissionForms?.forEach(formId => {
            defaultOrder.push({
              type: 'submissionForm',
              id: formId,
              order: orderIndex++
            });
          });
          
          // Add reinforcements in their current order
          course?.reinforcements?.forEach(reinforcementId => {
            defaultOrder.push({
              type: 'reinforcement',
              id: reinforcementId,
              order: orderIndex++
            });
          });
          
          setContentOrder(defaultOrder);
        }
      } else {
        setTitle('');
        setDescription('');
        setSelectedTags([]);
        setThumbnail(null);
        setThumbnailPreview('');
        setIncludedLessons([]);
        setAvailableLessons(safeLessons);
        setIncludedSubmissionForms([]);
        // Only non-repeatable submission forms for new course
        setAvailableSubmissionForms(safeForms.filter(sf => !sf.repeatable));
        setIncludedReinforcements([]);
        setAvailableReinforcements(safeReinforcements);
        setContentOrder([]);
      }
      setErrors({});
      setSearchQuery('');
      setShowSuccess(false);
    } finally {
      setIsLoading(false);
    }
  }, [mode, course, lessons, submissionForms, reinforcements]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isNew = mode === 'new';

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions if width exceeds MAX_WIDTH
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to Blob
          canvas.toBlob((blob) => {
            resolve(blob);
          }, file.type, QUALITY);
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const toggleTag = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
    setTagSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const removeTag = (tagId) => {
    setSelectedTags(prev => prev.filter(id => id !== tagId));
  };

  const getSelectedTagObjects = () => {
    return tags.filter(tag => selectedTags.includes(tag._id));
  };

  const getFilteredTags = () => {
    return tags.filter(tag => 
      !selectedTags.includes(tag._id) && 
      tag.title.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  };

  const handleTagInputClick = () => {
    setShowTagDropdown(true);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Backspace' && tagSearchTerm === '' && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
    }
  };

  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await processImageFile(file);
    }
  };

  const processImageFile = async (file) => {
    if (file.size > 5 * 1024 * 1024) { // 5MB
      setErrors({ ...errors, thumbnail: 'Image size must be less than 5MB' });
      return;
    }

    try {
      // Compress image first
      const compressedBlob = await compressImage(file);

      // Convert compressed image to Base64
      const reader = new FileReader();
      reader.onload = () => {
        setThumbnail({
          data: reader.result.split(',')[1], // Remove data URL prefix
          contentType: file.type
        });
        setThumbnailPreview(reader.result);
        thumbnailModified.current = true;
      };
      reader.readAsDataURL(compressedBlob);
      setErrors({ ...errors, thumbnail: null });
    } catch (error) {
      console.error('Error processing image:', error);
      setErrors({ ...errors, thumbnail: 'Error processing image. Please try again.' });
    }
  };

  // Drag and drop handlers for thumbnail
  const handleThumbnailDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverThumbnail(true);
  };

  const handleThumbnailDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverThumbnail(false);
  };

  const handleThumbnailDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverThumbnail(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await processImageFile(file);
      } else {
        setErrors({ ...errors, thumbnail: 'Please drop an image file' });
      }
    }
  };

  const handleLessonToggle = (lesson, isIncluded) => {
    if (isIncluded) {
      setIncludedLessons(includedLessons.filter(l => l._id !== lesson._id));
      setAvailableLessons([...availableLessons, lesson]);
      
      // Remove from content order
      setContentOrder(contentOrder.filter(item => !(item.type === 'lesson' && item.id === lesson._id)));
    } else {
      setIncludedLessons([...includedLessons, lesson]);
      setAvailableLessons(availableLessons.filter(l => l._id !== lesson._id));
      
      // Add to content order
      const maxOrder = contentOrder.length > 0 ? Math.max(...contentOrder.map(item => item.order)) : -1;
      setContentOrder([...contentOrder, {
        type: 'lesson',
        id: lesson._id,
        order: maxOrder + 1
      }]);
    }
  };

  const handleSubmissionFormToggle = (submissionForm, isIncluded) => {
    if (isIncluded) {
      setIncludedSubmissionForms(includedSubmissionForms.filter(sf => sf._id !== submissionForm._id));
      setAvailableSubmissionForms([...availableSubmissionForms, submissionForm]);
      
      // Remove from content order
      setContentOrder(contentOrder.filter(item => !(item.type === 'submissionForm' && item.id === submissionForm._id)));
    } else {
      setIncludedSubmissionForms([...includedSubmissionForms, submissionForm]);
      setAvailableSubmissionForms(availableSubmissionForms.filter(sf => sf._id !== submissionForm._id));
      
      // Add to content order
      const maxOrder = contentOrder.length > 0 ? Math.max(...contentOrder.map(item => item.order)) : -1;
      setContentOrder([...contentOrder, {
        type: 'submissionForm',
        id: submissionForm._id,
        order: maxOrder + 1
      }]);
    }
  };

  const handleReinforcementToggle = (reinforcement, isIncluded) => {
    if (isIncluded) {
      setIncludedReinforcements(includedReinforcements.filter(r => r._id !== reinforcement._id));
      setAvailableReinforcements([...availableReinforcements, reinforcement]);
      
      // Remove from content order
      setContentOrder(contentOrder.filter(item => !(item.type === 'reinforcement' && item.id === reinforcement._id)));
    } else {
      setIncludedReinforcements([...includedReinforcements, reinforcement]);
      setAvailableReinforcements(availableReinforcements.filter(r => r._id !== reinforcement._id));
      
      // Add to content order
      const maxOrder = contentOrder.length > 0 ? Math.max(...contentOrder.map(item => item.order)) : -1;
      setContentOrder([...contentOrder, {
        type: 'reinforcement',
        id: reinforcement._id,
        order: maxOrder + 1
      }]);
    }
  };

  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetItem) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.id === targetItem.id) return;

    const newOrder = [...contentOrder];
    const draggedIndex = newOrder.findIndex(item => item.id === draggedItem.id);
    const targetIndex = newOrder.findIndex(item => item.id === targetItem.id);

    // Remove dragged item
    const [dragged] = newOrder.splice(draggedIndex, 1);
    
    // Insert at target position
    newOrder.splice(targetIndex, 0, dragged);
    
    // Update order numbers
    newOrder.forEach((item, index) => {
      item.order = index;
    });

    setContentOrder(newOrder);
    setDraggedItem(null);
  };

  const getSearchPlaceholder = () => {
    switch (searchContext) {
      case 'included':
        return 'Search in included content...';
      case 'available':
        return 'Search in available content...';
      default:
        return 'Search all content...';
    }
  };

  const handleSearchContextChange = (context) => {
    setSearchContext(context);
    setIsSearchDropdownOpen(false);
  };

  const filteredAvailableLessons = searchQuery && (searchContext === 'all' || searchContext === 'available')
    ? availableLessons.filter(lesson =>
        lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'included' ? [] : availableLessons;

  const filteredIncludedLessons = searchQuery && (searchContext === 'all' || searchContext === 'included')
    ? includedLessons.filter(lesson =>
        lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'available' ? [] : includedLessons;

  const filteredAvailableSubmissionForms = searchQuery && (searchContext === 'all' || searchContext === 'available')
    ? availableSubmissionForms.filter(form =>
        form.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'included' ? [] : availableSubmissionForms;

  const filteredIncludedSubmissionForms = searchQuery && (searchContext === 'all' || searchContext === 'included')
    ? includedSubmissionForms.filter(form =>
        form.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'available' ? [] : includedSubmissionForms;

  const filteredAvailableReinforcements = searchQuery && (searchContext === 'all' || searchContext === 'available')
    ? availableReinforcements.filter(reinforcement =>
        reinforcement.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'included' ? [] : availableReinforcements;

  const filteredIncludedReinforcements = searchQuery && (searchContext === 'all' || searchContext === 'included')
    ? includedReinforcements.filter(reinforcement =>
        reinforcement.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : searchContext === 'available' ? [] : includedReinforcements;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const newErrors = {};
    if (!title.trim()) {
      newErrors.title = 'Course title is required';
    }
    if (includedLessons.length === 0 && includedSubmissionForms.length === 0 && includedReinforcements.length === 0) {
      newErrors.content = 'At least one lesson, submission form, or reinforcement is required';
    }

    // Check for consecutive reinforcements (warning only, not blocking)
    let hasConsecutiveReinforcements = false;
    if (contentOrder.length > 0) {
      // Sort content by order property to check actual sequence
      const sortedContent = [...contentOrder].sort((a, b) => a.order - b.order);

      // Check for consecutive reinforcements
      for (let i = 0; i < sortedContent.length - 1; i++) {
        if (sortedContent[i].type === 'reinforcement' && sortedContent[i + 1].type === 'reinforcement') {
          hasConsecutiveReinforcements = true;
          break;
        }
      }
    }

    // Set warning state for consecutive reinforcements
    setConsecutiveReinforcementWarning(hasConsecutiveReinforcements);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setErrors({});
    setIsSubmitting(true);
    
    try {
      const courseData = {
        title,
        description,
        lessons: JSON.stringify(includedLessons.map(l => l._id)),
        submissionForms: JSON.stringify(includedSubmissionForms.map(sf => sf._id)),
        reinforcements: JSON.stringify(includedReinforcements.map(r => r._id)),
        contentOrder: JSON.stringify(contentOrder),
        tags: JSON.stringify(selectedTags)
      };

      // Only send thumbnail if user actually changed it
      if (thumbnailModified.current && thumbnail?.data && thumbnail?.contentType) {
        courseData.thumbnail = thumbnail;
      }
      
      await onSubmit(courseData);
      
      setShowSuccess(true);
      setTimeout(() => {
        if (isNew) {
          setTitle('');
          setDescription('');
          setSelectedTags([]);
          setThumbnail(null);
          setThumbnailPreview('');
          setIncludedLessons([]);
          setAvailableLessons(lessons);
          setIncludedSubmissionForms([]);
          setAvailableSubmissionForms(submissionForms);
          setIncludedReinforcements([]);
          setAvailableReinforcements(reinforcements);
          setContentOrder([]);
          setShowSuccess(false);
        }
        onClose();
      }, 1500);
    } catch (error) {
      setErrors({ submit: 'Failed to save course. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContentItem = (item, index) => {
    const isLesson = item.type === 'lesson';
    const isReinforcement = item.type === 'reinforcement';
    const content = isLesson 
      ? includedLessons.find(l => l._id === item.id)
      : isReinforcement 
        ? includedReinforcements.find(r => r._id === item.id)
        : includedSubmissionForms.find(sf => sf._id === item.id);

    if (!content) return null;

    return (
      <div
        key={`${item.type}-${item.id}`}
        draggable={!isView}
        onDragStart={(e) => handleDragStart(e, item)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, item)}
        className={`group flex items-center gap-3 p-4 border-2 border-gray-200 rounded-lg mb-3 cursor-move hover:border-[#1086a4] hover:bg-[#1086a4]/5 transition-all duration-200 ${
          draggedItem?.id === item.id ? 'opacity-50 scale-95 shadow-lg' : ''
        } ${!isView ? 'hover:shadow-md' : ''}`}
      >
        {/* Drag Handle */}
        {!isView && (
          <div className="flex-shrink-0">
            <GripVertical className="h-5 w-5 text-gray-400 group-hover:text-[#1086a4] transition-colors cursor-grab active:cursor-grabbing" />
          </div>
        )}
        
        {/* Type Badge with Icon */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
            isLesson 
              ? 'bg-blue-100 text-blue-700 border border-blue-200' 
              : isReinforcement
                ? 'bg-orange-100 text-orange-700 border border-orange-200'
                : 'bg-purple-100 text-purple-700 border border-purple-200'
          }`}>
            {isLesson ? (
              <FileText className="h-3.5 w-3.5" />
            ) : isReinforcement ? (
              <Target className="h-3.5 w-3.5" />
            ) : (
              <ClipboardList className="h-3.5 w-3.5" />
            )}
            <span>{isLesson ? 'Lesson' : isReinforcement ? 'Reinforcement' : 'Form'}</span>
          </div>
        </div>
        
        {/* Content Title */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 truncate group-hover:text-[#1086a4] transition-colors">
            {content.title}
          </p>
          {isLesson && content.description && (
            <p className="text-xs text-gray-500 truncate mt-1">
              {content.description}
            </p>
          )}
        </div>
        
        {/* Order Number */}
        <div className="flex-shrink-0">
          <div className="flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full text-xs font-medium text-gray-600 group-hover:bg-[#1086a4] group-hover:text-white transition-colors">
            {index + 1}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full relative flex flex-col">
      {/* Redesigned Professional Header */}
      <div className="bg-gradient-to-r from-white via-blue-50/30 to-white border-b-2 border-[#1086a4] shadow-md">
        <div className="px-6 py-5">
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-1 hover:text-[#1086a4] transition-colors group"
            >
              <Home className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span className="font-medium">Content Management</span>
            </button>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <button
              type="button"
              onClick={onClose}
              className="hover:text-[#1086a4] transition-colors font-medium"
            >
              Courses
            </button>
            <ChevronRight className="h-4 w-4 text-gray-400" />
            <span className="text-[#1086a4] font-semibold">
              {isNew && 'New Course'}
              {isEdit && 'Edit Course'}
              {isView && 'View Course'}
            </span>
          </div>

          {/* Main Title Section with Icon and Badge */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#1086a4] to-[#054581] rounded-xl flex items-center justify-center shadow-lg">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-bold text-[#054581]">
                    {isNew && 'Create New Course'}
                    {isEdit && 'Edit Course'}
                    {isView && 'Course Details'}
                  </h2>
                  {/* Mode Badge */}
                  {isNew && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 text-xs font-bold rounded-full border border-purple-300 shadow-sm">
                      <Plus className="h-3 w-3" />
                      CREATING
                    </span>
                  )}
                  {isEdit && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-bold rounded-full border border-blue-300 shadow-sm">
                      <Edit2 className="h-3 w-3" />
                      EDITING
                    </span>
                  )}
                  {isView && (
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 text-xs font-bold rounded-full border border-green-300 shadow-sm">
                      <Eye className="h-3 w-3" />
                      VIEWING
                    </span>
                  )}
                </div>
                {/* Course Name (when editing or viewing) */}
                {(isEdit || isView) && course?.title && (
                  <p className="text-sm text-gray-600 font-medium">
                    <span className="text-gray-400">Course: </span>
                    <span className="text-[#1086a4]">{course.title}</span>
                  </p>
                )}
                {isNew && (
                  <p className="text-sm text-gray-500">Fill in the form below to create a new course</p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 text-gray-400 hover:text-[#1086a4] hover:bg-white/80 rounded-xl p-2.5 focus:outline-none transition-all shadow-sm hover:shadow-md border border-transparent hover:border-[#1086a4]"
              disabled={isSubmitting}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Checklist - Enhanced */}
          {!isView && (
            <div className="flex items-center gap-4 text-xs flex-wrap bg-white/60 backdrop-blur-sm rounded-lg px-4 py-3 border border-gray-200 shadow-sm">
              <span className="text-gray-500 font-semibold uppercase tracking-wide">Progress:</span>
              <div className={`flex items-center gap-1.5 ${title ? 'text-green-600' : 'text-gray-400'}`}>
                {title ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 border-2 border-current rounded-full" />}
                <span className="font-medium">Title</span>
              </div>
              <div className={`flex items-center gap-1.5 ${(includedLessons.length > 0 || includedSubmissionForms.length > 0 || includedReinforcements.length > 0) ? 'text-green-600' : 'text-gray-400'}`}>
                {(includedLessons.length > 0 || includedSubmissionForms.length > 0 || includedReinforcements.length > 0) ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 border-2 border-current rounded-full" />}
                <span className="font-medium">Content ({includedLessons.length + includedSubmissionForms.length + includedReinforcements.length})</span>
              </div>
              <div className={`flex items-center gap-1.5 ${selectedTags.length > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                {selectedTags.length > 0 ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 border-2 border-current rounded-full" />}
                <span className="font-medium">Tags ({selectedTags.length})</span>
              </div>
              <div className={`flex items-center gap-1.5 ${thumbnail ? 'text-green-600' : 'text-gray-300'}`}>
                {thumbnail ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-4 h-4 border-2 border-current rounded-full" />}
                <span className="font-medium">Thumbnail</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Form Content */}
      <form
        ref={formRef}
        className="flex-1"
        onSubmit={handleSubmit}
      >

        {/* Success Message */}
        {showSuccess && (
          <div className="p-4 bg-green-50 border-b border-green-200">
            <p className="text-green-700 text-sm font-medium">
              Course {isNew ? 'created' : 'updated'} successfully!
            </p>
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <p className="text-red-700 text-sm font-medium">{errors.submit}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="p-6 sm:p-8 pb-24">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1086a4]"></div>
            </div>
          ) : (
            <>
              {/* Title Field */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#054581] mb-2">Course Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.title 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  disabled={isView || isSubmitting}
                  placeholder="Enter course title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title}</p>
                )}
              </div>

              {/* Description Field */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#054581] mb-2">Course Description</label>
                <textarea
                  className={`w-full px-3 py-2 bg-[#f9fafb] border rounded-md text-gray-900 placeholder-gray-500 ${
                    errors.description 
                      ? 'border-red-500 focus:border-red-400' 
                      : 'border-[#1086a4] focus:border-[#1086a4]'
                  } focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors`}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  disabled={isView || isSubmitting}
                  placeholder="Enter course description (optional)"
                  rows={4}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              {/* Tags Field */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Tags {selectedTags.length > 0 && <span className="text-gray-500 font-normal">({selectedTags.length} selected)</span>}
                </label>
                {!isView && (
                  <div className="relative" ref={tagDropdownRef}>
                    <div
                      onClick={handleTagInputClick}
                      className="min-h-[42px] px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md cursor-text focus-within:ring-2 focus-within:ring-[#1086a4] focus-within:border-transparent transition-all"
                    >
                      <div className="flex flex-wrap items-center gap-1">
                        {getSelectedTagObjects().map(tag => (
                          <span
                            key={tag._id}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-20 text-[#1086a4] border border-[#1086a4] border-opacity-30"
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            {tag.title}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTag(tag._id);
                              }}
                              className="ml-1 text-[#1086a4] hover:text-[#054581] hover:bg-[#1086a4] hover:bg-opacity-20 rounded-full p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={tagSearchTerm}
                          onChange={(e) => setTagSearchTerm(e.target.value)}
                          onKeyDown={handleTagInputKeyDown}
                          onFocus={() => setShowTagDropdown(true)}
                          placeholder={selectedTags.length === 0 ? "Search and select tags..." : "Search tags..."}
                          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-gray-900 placeholder-gray-500 text-sm"
                        />
                      </div>
                    </div>
                    {showTagDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-[#1086a4] rounded-md shadow-lg max-h-48 overflow-auto">
                        {getFilteredTags().length > 0 ? (
                          getFilteredTags().map(tag => (
                            <button
                              key={tag._id}
                              type="button"
                              onClick={() => toggleTag(tag._id)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center"
                            >
                              <Tag className="w-3 h-3 mr-2 text-[#1086a4]" />
                              {tag.title}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            {tagSearchTerm ? `No tags found for "${tagSearchTerm}"` : 'No more tags available'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {isView && getSelectedTagObjects().length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {getSelectedTagObjects().map(tag => (
                      <span
                        key={tag._id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#1086a4] bg-opacity-10 text-[#1086a4]"
                      >
                        <Tag className="w-3 h-3 mr-1" />
                        {tag.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail Field */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-[#054581] mb-2">
                  Course Thumbnail
                  {thumbnailPreview && !isView && (
                    <button
                      type="button"
                      onClick={() => {
                        setThumbnail(null);
                        setThumbnailPreview('');
                        thumbnailModified.current = true;
                      }}
                      className="ml-3 text-xs text-red-500 hover:text-red-700 font-normal underline"
                    >
                      Remove Thumbnail
                    </button>
                  )}
                </label>
                <div
                  className={`mt-2 rounded-lg border-2 transition-all ${
                    dragOverThumbnail
                      ? 'border-[#1086a4] border-solid bg-blue-50'
                      : 'border-dashed border-gray-300 bg-white'
                  }`}
                  onDragOver={!isView ? handleThumbnailDragOver : undefined}
                  onDragLeave={!isView ? handleThumbnailDragLeave : undefined}
                  onDrop={!isView ? handleThumbnailDrop : undefined}
                >
                  <div className="p-6 flex flex-col sm:flex-row gap-6 items-center">
                    {/* Thumbnail Preview */}
                    <div className="w-full sm:w-1/2 max-w-sm">
                      {thumbnailPreview ? (
                        <div className="relative aspect-video w-full bg-gray-100 rounded-lg overflow-hidden group shadow-md">
                          <img
                            src={thumbnailPreview}
                            alt="Course thumbnail"
                            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="absolute inset-0 flex items-center justify-center bg-gray-100">
                                  <div class="text-center">
                                    <ImageIcon class="mx-auto h-12 w-12 text-gray-400" />
                                    <p class="mt-2 text-sm text-gray-500">Error loading image</p>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : (
                        <div className="aspect-video w-full bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300">
                          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <p className="text-sm text-gray-500 mt-2">No thumbnail</p>
                        </div>
                      )}
                    </div>

                    {/* Upload Area */}
                    <div className="w-full sm:w-1/2 flex flex-col justify-center">
                      {!isView && (
                        <>
                          {dragOverThumbnail ? (
                            <div className="text-center py-8">
                              <ImageIcon className="mx-auto h-16 w-16 text-[#1086a4] animate-bounce" />
                              <p className="text-lg font-semibold text-[#1086a4] mt-4">Drop image here!</p>
                            </div>
                          ) : (
                            <>
                              <div className="flex flex-col items-center">
                                <label
                                  htmlFor="thumbnail-upload"
                                  className="cursor-pointer px-6 py-3 bg-[#1086a4] text-white rounded-lg font-semibold hover:bg-[#054581] transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                                >
                                  <ImageIcon className="h-5 w-5" />
                                  <span>{thumbnailPreview ? 'Change Image' : 'Upload Image'}</span>
                                  <input
                                    id="thumbnail-upload"
                                    name="thumbnail"
                                    type="file"
                                    accept="image/*"
                                    className="sr-only"
                                    onChange={handleThumbnailChange}
                                    disabled={isSubmitting}
                                  />
                                </label>
                                <p className="text-sm text-gray-500 mt-4 text-center">
                                  or drag and drop an image here
                                </p>
                              </div>
                              <div className="mt-6 space-y-2 text-xs text-gray-500">
                                <p className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  Supported: PNG, JPG, GIF
                                </p>
                                <p className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  Maximum size: 5MB
                                </p>
                                <p className="flex items-center gap-2">
                                  <Check className="h-4 w-4 text-green-500" />
                                  Recommended: 16:9 aspect ratio
                                </p>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {errors.thumbnail && (
                  <p className="text-red-500 text-sm mt-1">{errors.thumbnail}</p>
                )}
              </div>

              {/* Content Order Section */}
              {contentOrder.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#1086a4] to-[#054581] rounded-lg flex items-center justify-center shadow-md">
                        <GripVertical className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#054581]">Content Order</h3>
                        <p className="text-sm text-gray-600">
                          {!isView ? 'Drag items to reorder your course content' : 'Course content in display order'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsContentOrderCollapsed(!isContentOrderCollapsed)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isContentOrderCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                    </button>
                  </div>

                  {/* Warning Banner for Consecutive Reinforcements */}
                  {!isView && includedReinforcements.length > 0 && consecutiveReinforcementWarning && (
                    <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <Target className="h-5 w-5 text-yellow-600 mt-0.5" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-yellow-800 mb-1">Consecutive Reinforcements Detected</p>
                          <p className="text-sm text-yellow-700">
                            Warning: You have placed two or more reinforcements consecutively. The system does not fully support this pattern and may not display them as expected. Consider placing lessons or submission forms between reinforcements for optimal user experience.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isContentOrderCollapsed && (
                    <div>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <FileText className="h-4 w-4 text-blue-600" />
                          <span>{contentOrder.filter(item => item.type === 'lesson').length} Lessons</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ClipboardList className="h-4 w-4 text-purple-600" />
                          <span>{contentOrder.filter(item => item.type === 'submissionForm').length} Forms</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Target className="h-4 w-4 text-orange-600" />
                          <span>{contentOrder.filter(item => item.type === 'reinforcement').length} Reinforcements</span>
                        </div>
                      </div>
                      <div className="border-2 border-[#1086a4] rounded-xl p-5 bg-gradient-to-br from-gray-50 to-blue-50/30 shadow-sm">
                        {[...contentOrder].sort((a, b) => a.order - b.order).map((item, index) => renderContentItem(item, index))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Content Management Section */}
              <div className="space-y-8">
                {/* Lessons Section */}
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#054581]">Course Lessons</h3>
                        <p className="text-sm text-gray-600">
                          Select lessons to include in this course
                          {(includedLessons.length > 0) && (
                            <span className="ml-2 text-blue-600 font-semibold">({includedLessons.length} included)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {errors.lessons && (
                        <p className="text-red-500 text-sm font-medium">Required!</p>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsLessonsCollapsed(!isLessonsCollapsed)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {isLessonsCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {!isLessonsCollapsed && (
                  <div>

                  {/* Search Box with Dropdown */}
                  <div className="relative">
                    <div className="relative flex">
                      {/* Search Context Dropdown Button */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsSearchDropdownOpen(!isSearchDropdownOpen)}
                          className="h-full px-3 py-2 bg-[#1086a4] text-white rounded-l-md border-r border-white/20 hover:bg-[#054581] transition-colors flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-[#1086a4]"
                          disabled={isView || isSubmitting}
                        >
                          <Search className="h-5 w-5" />
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        
                        {/* Dropdown Menu */}
                        {isSearchDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200">
                            <div className="py-1">
                              <button
                                type="button"
                                onClick={() => handleSearchContextChange('all')}
                                className={`w-full text-left px-4 py-2 text-sm ${
                                  searchContext === 'all'
                                    ? 'bg-[#1086a4] text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                All Content
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSearchContextChange('included')}
                                className={`w-full text-left px-4 py-2 text-sm ${
                                  searchContext === 'included'
                                    ? 'bg-[#1086a4] text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Included Content
                              </button>
                              <button
                                type="button"
                                onClick={() => handleSearchContextChange('available')}
                                className={`w-full text-left px-4 py-2 text-sm ${
                                  searchContext === 'available'
                                    ? 'bg-[#1086a4] text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                              >
                                Available Content
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Search Input */}
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          className="w-full pl-3 pr-10 py-2 border border-l-0 border-[#1086a4] rounded-r-md bg-[#f9fafb] text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-transparent transition-colors"
                          placeholder={getSearchPlaceholder()}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          disabled={isView || isSubmitting}
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Lesson Lists */}
                  <div className="space-y-4">
                    {/* Included Lessons */}
                    <div className={searchContext === 'available' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <Plus className="h-3 w-3 text-green-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Included Lessons ({includedLessons.length})</h4>
                        </div>
                      </div>
                      <div className={`border rounded-md divide-y max-h-[200px] overflow-y-auto ${
                        errors.lessons 
                          ? 'border-red-500 divide-red-500' 
                          : 'border-[#1086a4] divide-[#1086a4]'
                      }`}>
                        {filteredIncludedLessons.length > 0 ? (
                                                  filteredIncludedLessons.map(lesson => (
                          <div
                            key={lesson._id}
                            className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-[#1086a4] bg-blue-50/30"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <FileText className="h-4 w-4 text-[#1086a4]" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {lesson.title}
                                </p>
                                {lesson.description && (
                                  <p className="text-xs text-gray-500 truncate mt-1">
                                    {lesson.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleLessonToggle(lesson, true)}
                                className="ml-2 p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                                title="Remove from course"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))
                        ) : (
                          <div className={`p-4 text-center text-sm ${
                            errors.lessons ? 'text-red-500' : 'text-gray-500'
                          }`}>
                            {searchQuery ? 'No matching lessons found' : 'No lessons added yet'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Available Lessons */}
                    <div className={searchContext === 'included' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                            <FileText className="h-3 w-3 text-gray-500" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Available Lessons ({availableLessons.length})</h4>
                        </div>
                      </div>
                      <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                        {filteredAvailableLessons.length > 0 ? (
                                                  filteredAvailableLessons.map(lesson => (
                          <div
                            key={lesson._id}
                            className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-gray-300"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <FileText className="h-4 w-4 text-gray-500" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {lesson.title}
                                </p>
                                {lesson.description && (
                                  <p className="text-xs text-gray-500 truncate mt-1">
                                    {lesson.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleLessonToggle(lesson, false)}
                                className="ml-2 p-2 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                                title="Add to course"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchQuery ? 'No matching lessons found' : 'No lessons available. Please create lessons first.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                  )}
                </div>

                {/* Submission Forms Section - Admin Only */}
                {userRole === 'admin' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <ClipboardList className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#054581]">Course Submission Forms</h3>
                        <p className="text-sm text-gray-600">Select submission forms to include in this course</p>
                      </div>
                    </div>
                  </div>

                  {/* Submission Form Lists */}
                  <div className="space-y-4">
                    {/* Included Submission Forms */}
                    <div className={searchContext === 'available' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <Plus className="h-3 w-3 text-green-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Included Submission Forms ({includedSubmissionForms.length})</h4>
                        </div>
                      </div>
                      <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                        {filteredIncludedSubmissionForms.length > 0 ? (
                                                  filteredIncludedSubmissionForms.map(submissionForm => (
                          <div
                            key={submissionForm._id}
                            className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-purple-500 bg-purple-50/30"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                  <ClipboardList className="h-4 w-4 text-purple-600" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {submissionForm.title}
                                </p>
                              </div>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleSubmissionFormToggle(submissionForm, true)}
                                className="ml-2 p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                                title="Remove from course"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchQuery ? 'No matching submission forms found' : 'No submission forms added yet'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Available Submission Forms */}
                    <div className={searchContext === 'included' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                            <ClipboardList className="h-3 w-3 text-gray-500" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Available Submission Forms ({availableSubmissionForms.length})</h4>
                        </div>
                      </div>
                      <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                        {filteredAvailableSubmissionForms.length > 0 ? (
                                                  filteredAvailableSubmissionForms.map(submissionForm => (
                          <div
                            key={submissionForm._id}
                            className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-gray-300"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <ClipboardList className="h-4 w-4 text-gray-500" />
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {submissionForm.title}
                                </p>
                              </div>
                            </div>
                            {!isView && (
                              <button
                                type="button"
                                onClick={() => handleSubmissionFormToggle(submissionForm, false)}
                                className="ml-2 p-2 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                                title="Add to course"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchQuery ? 'No matching submission forms found' : 'No submission forms available. Please create submission forms first.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Reinforcements Section */}
                <div className="bg-white border-2 border-orange-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                        <Target className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#054581]">Course Reinforcements</h3>
                        <p className="text-sm text-gray-600">
                          Select reinforcements to include in this course
                          {(includedReinforcements.length > 0) && (
                            <span className="ml-2 text-orange-600 font-semibold">({includedReinforcements.length} included)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setIsReinforcementsCollapsed(!isReinforcementsCollapsed)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isReinforcementsCollapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                    </button>
                  </div>

                  {!isReinforcementsCollapsed && (
                  <div>

                  {/* Reinforcement Lists */}
                  <div className="space-y-4">
                    {/* Included Reinforcements */}
                    <div className={searchContext === 'available' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                            <Plus className="h-3 w-3 text-green-600" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Included Reinforcements ({includedReinforcements.length})</h4>
                        </div>
                      </div>
                      <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                        {filteredIncludedReinforcements.length > 0 ? (
                          filteredIncludedReinforcements.map(reinforcement => (
                            <div
                              key={reinforcement._id}
                              className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-orange-500 bg-orange-50/30"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Target className="h-4 w-4 text-orange-600" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {reinforcement.title}
                                  </p>
                                </div>
                              </div>
                              {!isView && (
                                <button
                                  type="button"
                                  onClick={() => handleReinforcementToggle(reinforcement, true)}
                                  className="ml-2 p-2 text-red-500 hover:text-red-700 rounded-full hover:bg-red-50 transition-colors"
                                  title="Remove from course"
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchQuery ? 'No matching reinforcements found' : 'No reinforcements added yet'}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Available Reinforcements */}
                    <div className={searchContext === 'included' ? 'opacity-50' : ''}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                            <Target className="h-3 w-3 text-gray-500" />
                          </div>
                          <h4 className="text-sm font-semibold text-[#054581]">Available Reinforcements ({availableReinforcements.length})</h4>
                        </div>
                      </div>
                      <div className="border border-[#1086a4] rounded-md divide-y divide-[#1086a4] max-h-[200px] overflow-y-auto">
                        {filteredAvailableReinforcements.length > 0 ? (
                          filteredAvailableReinforcements.map(reinforcement => (
                            <div
                              key={reinforcement._id}
                              className="flex items-center justify-between p-3 hover:bg-[#f9fafb] transition-colors border-l-4 border-l-gray-300"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <Target className="h-4 w-4 text-gray-500" />
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 truncate">
                                    {reinforcement.title}
                                  </p>
                                </div>
                              </div>
                              {!isView && (
                                <button
                                  type="button"
                                  onClick={() => handleReinforcementToggle(reinforcement, false)}
                                  className="ml-2 p-2 text-green-500 hover:text-green-700 rounded-full hover:bg-green-50 transition-colors"
                                  title="Add to course"
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            {searchQuery ? 'No matching reinforcements found' : 'No reinforcements available. Please create reinforcements first.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

      </form>

      {/* Floating Action Buttons - Sticky Footer */}
      {(isNew || isEdit) && (
        <div className="sticky bottom-0 z-20 bg-white border-t-2 border-[#1086a4] shadow-lg">
          <div className="px-6 py-4">
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end items-center">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto px-6 py-3 border-2 border-[#1086a4] rounded-lg text-[#1086a4] font-semibold hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="course-form"
                onClick={handleSubmit}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-[#1086a4] to-[#054581] text-white rounded-lg font-semibold hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {isNew ? 'Adding Course...' : 'Saving Changes...'}
                  </>
                ) : (
                  <>
                    <Check className="h-5 w-5 mr-2" />
                    {isNew ? 'Add Course' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          type="button"
          onClick={scrollToTop}
          className="fixed bottom-24 right-8 z-30 p-3 bg-[#1086a4] text-white rounded-full shadow-lg hover:bg-[#054581] hover:shadow-xl transition-all transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:ring-offset-2"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  tag: state.tag
});

export default connect(mapStateToProps, { getTags })(CourseForm);
